import axios, { AxiosInstance } from 'axios';
import { API_BASE } from './config';
import { DashboardData, RiskHistory, AuthResponse, AdminCredentials } from '../types';
import logger from './logger';
import appDebugger from './debugger';

// Centralized API base
const API_URL = API_BASE;

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage on initialization
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      this.token = storedToken;
      logger.info('AUTH', 'Token loaded from localStorage on API service initialization');
    }

    // Add token to requests if available and log requests
    this.api.interceptors.request.use((config) => {
      const startTime = Date.now();
      (config as any).metadata = { startTime };
      
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      
      logger.apiRequest(config.method?.toUpperCase() || 'GET', config.url || '');
      appDebugger.trackAPICall(config.url || '', config.method?.toUpperCase() || 'GET');
      return config;
    });

    // Handle token expiration with automatic refresh
    this.api.interceptors.response.use(
      (response) => {
        const duration = (response.config as any).metadata?.startTime 
          ? Date.now() - (response.config as any).metadata.startTime 
          : undefined;
        
        logger.apiResponse(
          response.config.method?.toUpperCase() || 'GET',
          response.config.url || '',
          response.status,
          duration
        );
        
        return response;
      },
      async (error) => {
        logger.apiError(
          error.config?.method?.toUpperCase() || 'GET',
          error.config?.url || '',
          error
        );
        
        // Handle 401 errors with automatic token refresh
        if (error.response?.status === 401 && !error.config._retry) {
          const refreshToken = localStorage.getItem('refreshToken');
          
          if (refreshToken && !error.config.url?.includes('/api/admin/refresh')) {
            error.config._retry = true;
            
            try {
              logger.info('AUTH', 'Attempting automatic token refresh');
              const refreshResult = await this.refreshToken(refreshToken);
              
              // Update the failed request with new token
              error.config.headers.Authorization = `Bearer ${refreshResult.accessToken}`;
              
              // Retry the original request
              return this.api(error.config);
            } catch (refreshError) {
              logger.warn('AUTH', 'Token refresh failed, clearing tokens');
              this.clearToken();
              localStorage.removeItem('refreshToken');
              // Let the auth hook handle redirecting to login
            }
          } else {
            logger.warn('AUTH', 'Authentication failed, clearing token');
            this.clearToken();
            if (refreshToken) {
              localStorage.removeItem('refreshToken');
            }
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    if (this.token === token) {
      return; // Prevent duplicate token setting
    }
    this.token = token;
    localStorage.setItem('authToken', token);
    logger.info('AUTH', 'Token set');
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    logger.info('AUTH', 'Tokens cleared');
    logger.clearUserId();
  }

  async getDashboard(): Promise<DashboardData> {
    const response = await this.api.get('/api/dashboard');
    return response.data;
  }

  async getRiskHistory(riskType: string, days: number = 7): Promise<RiskHistory> {
    const response = await this.api.get(`/api/risks/${riskType}/history`, {
      params: { days }
    });
    return response.data;
  }

  async login(credentials: AdminCredentials): Promise<AuthResponse> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await this.api.post('/api/admin/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const authData = response.data;
    this.setToken(authData.access_token);
    
    // Store refresh token for automatic refresh
    if (authData.refresh_token) {
      localStorage.setItem('refreshToken', authData.refresh_token);
    }
    
    logger.setUserId(credentials.username);
    logger.authentication('login_success', { username: credentials.username });
    return authData;
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await this.api.post('/api/admin/refresh', {
      refreshToken: refreshToken,
    });

    const authData = response.data;
    this.setToken(authData.accessToken);
    
    // Update stored refresh token
    if (authData.refreshToken) {
      localStorage.setItem('refreshToken', authData.refreshToken);
    }
    
    logger.info('AUTH', 'Token refreshed successfully');
    return authData;
  }

  async exportReport(format: 'pdf' | 'json' = 'json'): Promise<Blob> {
    const response = await this.api.get('/api/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  async getSystemStatus(): Promise<any> {
    const response = await this.api.get('/api/admin/stats');
    return response.data;
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.put('/api/admin/password', { oldPassword, newPassword });
    return response.data;
  }

  async updateRiskData(riskType: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/admin/api/risks/${riskType}`, data);
    return response.data;
  }

  async getNewsSources(): Promise<any[]> {
    const response = await this.api.get('/api/admin/news-sources');
    return response.data;
  }

  async validateRSSFeed(url: string): Promise<{ valid: boolean; error?: string; title?: string; description?: string }> {
    const response = await this.api.post('/api/admin/news-sources/validate-rss', { url });
    return response.data;
  }

  async testScraping(url: string, scrapingConfig?: { selectors: string[]; fallback?: string }): Promise<any> {
    const response = await this.api.post('/api/admin/news-sources/test-scraping', { url, scrapingConfig });
    return response.data;
  }

  async createNewsSource(newsSourceData: any): Promise<any> {
    const response = await this.api.post('/api/admin/news-sources', newsSourceData);
    return response.data;
  }

  async updateNewsSource(sourceId: number, newsSourceData: any): Promise<any> {
    const response = await this.api.put(`/api/admin/news-sources/${sourceId}`, newsSourceData);
    return response.data;
  }

  async toggleNewsSource(sourceId: number): Promise<any> {
    const response = await this.api.post(`/api/admin/news-sources/${sourceId}/toggle`);
    return response.data;
  }

  async deleteNewsSource(sourceId: number): Promise<{ deleted: boolean; articlesDeleted: number; message: string }> {
    const response = await this.api.delete(`/api/admin/news-sources/${sourceId}`);
    return response.data;
  }

  async getRecentArticles(sourceId?: number, limit = 50, offset = 0): Promise<any> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    if (sourceId) {
      params.append('sourceId', sourceId.toString());
    }
    
    const url = sourceId 
      ? `/api/admin/articles/source/${sourceId}?${params.toString()}`
      : `/api/admin/articles/recent?${params.toString()}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getArticlesByDate(date: string, countryCode: string = 'EST', limit = 20): Promise<any> {
    const response = await this.api.get('/api/admin/articles/by-date', {
      params: { date, countryCode, limit: limit.toString() }
    });
    return response.data;
  }

  async getQueueStats(): Promise<any> {
    const response = await this.api.get('/api/admin/queue/stats');
    return response.data;
  }

  async getCostData(): Promise<any> {
    const response = await this.api.get('/api/admin/costs');
    return response.data;
  }

  async clearCaches(): Promise<any> {
    const response = await this.api.post('/api/admin/clear-cache');
    return response.data;
  }

  // Cache management for testing
  async getCacheContents(): Promise<{
    cache: Array<{
      url: string;
      contentLength: number;
      contentPreview: string;
      cachedAt: string;
      ageMinutes: number;
    }>;
    totalEntries: number;
    totalSize: number;
  }> {
    const response = await this.api.get('/api/admin/testing/cache');
    return response.data;
  }

  async clearScrapingCache(url?: string): Promise<{ message: string }> {
    const response = await this.api.post('/api/admin/testing/clear-cache', 
      url ? { url } : undefined
    );
    return response.data;
  }

  async triggerNewsScraping(sourceId?: number): Promise<any> {
    const response = await this.api.post('/api/admin/trigger/news-scraping', { sourceId });
    return response.data;
  }

  async triggerRiskCalculation(): Promise<any> {
    // Use text response to avoid HTTP2 framing issues with empty JSON
    const response = await this.api.post('/api/admin/trigger/risk-calculation', undefined, { responseType: 'text' });
    try { return JSON.parse(response.data); } catch { return { message: String(response.data || 'ok') }; }
  }

  async triggerRiskBackfillRange(params?: { startDate?: string; endDate?: string }): Promise<{ enqueued: number; startDate?: string; endDate?: string; message?: string }> {
    const response = await this.api.post('/api/admin/trigger/risk-calculation-range', params || {}, { responseType: 'json' });
    return response.data;
  }

  // Testing endpoints
  async getHistoricalArticles(startDate: string, endDate: string, sourceIds?: number[]): Promise<any> {
    const params: any = { startDate, endDate };
    if (sourceIds) {
      params.sourceIds = sourceIds.join(',');
    }
    const response = await this.api.get('/api/admin/testing/historical', { params });
    return response.data;
  }

  async testHistoricalArticles(data: {
    articleIds: number[];
    countryCode: string;
    tier?: number;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/testing/historical/test', data);
    return response.data;
  }

  async getTestScenarios(): Promise<any> {
    const response = await this.api.get('/api/admin/testing/scenarios');
    return response.data;
  }

  async createTestScenario(scenario: any): Promise<any> {
    const response = await this.api.post('/api/admin/testing/scenarios', scenario);
    return response.data;
  }

  async testScenario(scenarioId: string, params: {
    countryCode: string;
    tier?: number;
    customPrompt?: string;
  }): Promise<any> {
    const response = await this.api.post(`/api/admin/testing/scenarios/${scenarioId}/test`, params);
    return response.data;
  }

  async backfillTranslations(data: { languages?: string[]; limit?: number; dryRun?: boolean; startAfterId?: number; budgetCapUsd?: number; onlyNewLanguages?: boolean }): Promise<{ scanned: number; updated: number; skipped: number; errors: string[]; languagesUsed?: string[] }> {
    const response = await this.api.post('/api/admin/testing/backfill/translations', data);
    return response.data;
  }

  async getBackfillPending(): Promise<{ totalAnalyses: number; pending: number; complete: number; languages: string[] }> {
    const response = await this.api.get('/api/admin/testing/backfill/translations/pending');
    return response.data;
  }

  async testPromptVariations(data: {
    article: any;
    countryCode: string;
    prompts: {
      tier1?: string;
      tier2?: string;
      tier3?: string;
    };
  }): Promise<any> {
    const response = await this.api.post('/api/admin/testing/prompts/test', data);
    return response.data;
  }

  async getPromptVersions(tier?: number): Promise<any> {
    const params = tier ? { tier } : {};
    const response = await this.api.get('/api/admin/testing/prompts/versions', { params });
    return response.data;
  }

  async savePromptVersion(data: {
    tier: number;
    prompt: string;
    version: string;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/testing/prompts/versions', data);
    return response.data;
  }

  async getCurrentWeights(): Promise<any> {
    const response = await this.api.get('/api/admin/testing/weights');
    return response.data;
  }

  async updateRiskWeights(weights: {
    military: number;
    diplomatic: number;
    economic: number;
    informationWarfare: number;
    cyber: number;
    energy: number;
  }): Promise<any> {
    const response = await this.api.put('/api/admin/testing/weights', weights);
    return response.data;
  }

  async testWeightConfiguration(data: {
    articleIds: number[];
    weights: any;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/testing/weights/test', data);
    return response.data;
  }

  async triggerArticleAnalysis(data: {
    articleId: number;
    countryCode?: string;
    tier?: number;
    useRelevanceCheck?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/trigger/article-analysis', data);
    return response.data;
  }

  async triggerBulkAnalysis(data: {
    countryCode?: string;
    useRelevanceCheck?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/trigger/bulk-analysis', data);
    return response.data;
  }

  async deleteArticleAnalysis(articleId: number, countryCode: string = 'EST'): Promise<{ deleted: boolean; message: string }> {
    const response = await this.api.delete(`/api/admin/articles/${articleId}/analysis`, {
      params: { countryCode }
    });
    return response.data;
  }

  async bulkDeleteArticles(articleIds: number[]): Promise<{ deleted: number; message: string }> {
    const response = await this.api.delete('/api/admin/articles/bulk', {
      data: { articleIds }
    });
    return response.data;
  }

  async deleteAllArticles(): Promise<{ deleted: number; message: string }> {
    const response = await this.api.delete('/api/admin/articles/bulk', {
      data: { deleteAll: true, confirm: 'DELETE_ALL_ARTICLES' }
    });
    return response.data;
  }

  async clearAllAnalysisData(): Promise<{ analysesCleared: number; relevanceCleared: number; message: string }> {
    const response = await this.api.delete('/api/admin/articles/analysis-data/all', {
      params: { confirm: 'CLEAR_ALL_ANALYSIS' }
    });
    return response.data;
  }

  // Threat Categories Management
  async getCategories(includeDeleted: boolean = false): Promise<any[]> {
    const response = await this.api.get('/api/admin/categories', {
      params: { includeDeleted }
    });
    return response.data;
  }

  async createCategory(categoryData: any): Promise<any> {
    const response = await this.api.post('/api/admin/categories', categoryData);
    return response.data;
  }

  async updateCategory(id: number, categoryData: any): Promise<any> {
    const response = await this.api.put(`/api/admin/categories/${id}`, categoryData);
    return response.data;
  }

  async deleteCategory(id: number, permanent: boolean = false): Promise<any> {
    const response = await this.api.delete(`/api/admin/categories/${id}`, {
      params: { permanent }
    });
    return response.data;
  }

  async restoreCategory(id: number): Promise<any> {
    const response = await this.api.post(`/api/admin/categories/${id}/restore`);
    return response.data;
  }

  // Threat Actors Management
  async getThreatActors(): Promise<any[]> {
    const response = await this.api.get('/api/admin/threat-actors');
    return response.data;
  }

  async createThreatActor(actorData: any): Promise<any> {
    const response = await this.api.post('/api/admin/threat-actors', actorData);
    return response.data;
  }

  async updateThreatActor(id: number, actorData: any): Promise<any> {
    const response = await this.api.put(`/api/admin/threat-actors/${id}`, actorData);
    return response.data;
  }

  async deleteThreatActor(id: number): Promise<any> {
    const response = await this.api.delete(`/api/admin/threat-actors/${id}`);
    return response.data;
  }

  async getCountryRiskHistory(countryCode: string, days: number): Promise<any> {
    const response = await this.api.get('/api/admin/risk-history', {
      params: { countryCode, days }
    });
    return response.data;
  }

  async compareModels(articleId: number, modelConfig: { model: string; tier: number }): Promise<any> {
    const response = await this.api.post('/api/admin/testing/compare-models', {
      articleId,
      modelConfig
    });
    return response.data;
  }

  // Generic methods for new endpoints
  async get(url: string, config?: any): Promise<any> {
    const response = await this.api.get(url, config);
    return response;
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.api.post(url, data, config);
    return response;
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    const response = await this.api.put(url, data, config);
    return response;
  }

  async delete(url: string, config?: any): Promise<any> {
    const response = await this.api.delete(url, config);
    return response;
  }
}

const apiService = new ApiService();
export default apiService;
